/**
 * <Search />
 *
 * A focused, accessible search input that commits to the URL only on an
 * explicit user trigger (Enter key or search icon click).  The component
 * keeps a piece of *local* draft state so the user can type freely without
 * firing API calls on every keystroke.
 *
 * Props:
 *   value       — the *committed* search term (comes from URL / parent hook).
 *                 Used to sync the input when the URL changes externally
 *                 (e.g. browser back button).
 *   onSearch    — called with the final term when the user commits.
 *                 Called with '' when the user explicitly clears.
 */

import { useState, useRef, useEffect, type KeyboardEvent } from 'react'
import { Search as SearchIcon, X } from 'lucide-react'

interface SearchProps {
  /** Committed search term from URL – drives the input when externally changed */
  value: string
  /** Fires when user commits (Enter / icon click) or explicitly clears */
  onSearch: (term: string) => void
  placeholder?: string
  className?: string
}

export default function Search({
  value,
  onSearch,
  placeholder = 'Search...',
  className = '',
}: SearchProps) {
  // Local draft — what the user is currently typing (not yet committed)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  // Keep draft in sync when the URL value changes externally
  // (e.g., user presses browser back/forward)
  useEffect(() => {
    setDraft(value)
  }, [value])

  const commit = () => {
    onSearch(draft.trim())
  }

  const clear = () => {
    setDraft('')
    onSearch('')
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commit()
    if (e.key === 'Escape') clear()
  }

  const showClear = draft.length > 0

  return (
    <div className={`relative lg:min-w-96 ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label="Search"
        className="w-full h-10 pl-4 pr-20 bg-gray-50 text-lg font-normal font-satoshi text-gray-900 placeholder-gray-600 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-wine-100 focus:border-transparent"
      />

      <div className="absolute right-0 top-0 flex h-10 items-stretch ">
   

        {showClear ? (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear search"
            className="w-10 h-10  flex items-center justify-center hover:bg-wine-200 group transition-colors"
          >
            <X className="w-5 h-5 group-hover:text-white" />
          </button>
        ) : null}

             <button
          type="button"
          onClick={commit}
          aria-label="Submit search"
          className="w-10 h-10 bg-wine-100 rounded-r flex items-center justify-center hover:bg-wine-200 transition-colors"
        >
          <SearchIcon className="w-5 h-5 text-white" />
        </button>
      </div>
    </div>
  )
}
